/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
    useContext, useEffect, useRef, useState, useMemo
} from 'react';
import { useLocation } from 'react-router-dom';
import CustomAlertProvider, { CustomAlertContext } from './CustomAlertContext';
import Button from '../UIElements/Button';
import Input from '../UIElements/Input';

import './style.scss';


function useCustomAlert() {
    const customAlertObject = useContext(CustomAlertContext);

    const customAlertMethods = {
        // for successful messages
        // options: { headline, content, buttonText, buttonIcon, closeCallback,
        // headlineIcon, headlineIconColor: red|green|blue|default }
        success: (options) => {
            customAlertObject.initialize({
                headline: options.headline,
                boxType: 'success',
                content: options.content,
                buttonText: options.buttonText || 'Ok',
                buttonIcon: options.buttonIcon || null,
                closeCallback: options.closeCallback || null,
                headlineIcon: options.headlineIcon,
                headlineIconColor: options.headlineIconColor
            });
        },
        // for error messages
        // options: { headline, content, buttonText, buttonIcon, closeCallback,
        // headlineIcon, headlineIconColor: red|green|blue|default}
        error: (options) => {
            customAlertObject.initialize({
                headline: options.headline,
                boxType: 'error',
                content: options.content,
                buttonText: options.buttonText || 'Ok',
                buttonIcon: options.buttonIcon || null,
                closeCallback: options.closeCallback || null,
                headlineIcon: options.headlineIcon,
                headlineIconColor: options.headlineIconColor
            });
        },
        // for action fail messages (with a red confirm button and transparent cancel button)
        // options: { headline, content, buttonText, buttonIcon, closeCallback,
        // secondaryButtonText, secondaryButtonIcon, secondaryCloseCallback,
        // headlineIcon, headlineIconColor: red|green|blue|default}
        fail: (options) => {
            customAlertObject.initialize({
                headline: options.headline,
                boxType: 'fail',
                content: options.content,
                buttonText: options.buttonText || 'Give up',
                buttonIcon: options.buttonIcon || null,
                closeCallback: options.closeCallback || null,
                secondaryButtonText: options.secondaryButtonText || 'Retry',
                secondaryButtonIcon: options.secondaryButtonIcon || null,
                secondaryCloseCallback: options.secondaryCloseCallback || null,
                headlineIcon: options.headlineIcon,
                headlineIconColor: options.headlineIconColor
            });
        },
        // for info messages
        // options: { headline, content, buttonText, buttonIcon, closeCallback,
        // headlineIcon, headlineIconColor: red|green|blue|default }
        inform: (options) => {
            customAlertObject.initialize({
                headline: options.headline,
                boxType: 'inform',
                content: options.content,
                buttonText: options.buttonText || 'Ok',
                buttonIcon: options.buttonIcon || null,
                closeCallback: options.closeCallback || null,
                headlineIcon: options.headlineIcon,
                headlineIconColor: options.headlineIconColor
            });
        },
        // popup layout to show something with a close button
        // options: { headline, content, closeCallback, headlineIcon, headlineIconColor: red|green|blue|default}
        popup: (options) => {
            customAlertObject.initialize({
                headline: options.headline,
                boxType: 'popup',
                content: options.content,
                closeCallback: options.closeCallback || null,
                headlineIcon: options.headlineIcon,
                headlineIconColor: options.headlineIconColor
            });
        },
        // for confirmation popups (with a green confirm and transparent cancel button)
        // options: { headline, content, confirmButtonText, confirmButtonIcon, cancelButtonText,
        // cancelButtonIcon, headlineIcon, headlineIconColor: red|green|blue|default}
        confirm: (options) => new Promise((resolve) => {
            customAlertObject.initialize({
                headline: options.headline,
                actionCallback: resolve,
                boxType: 'confirm',
                content: options.content,
                confirmButtonText: options.confirmButtonText || 'Confirm',
                confirmButtonIcon: options.confirmButtonIcon || null,
                cancelButtonText: options.cancelButtonText || 'Cancel',
                cancelButtonIcon: options.cancelButtonIcon || null,
                headlineIcon: options.headlineIcon,
                headlineIconColor: options.headlineIconColor
            });
        }),
        // for dangerous confirmation popups like deleting something with a
        //  red confirm button and transparent cancel button
        // options: { headline, content, confirmButtonText, confirmButtonIcon,
        // cancelButtonText, cancelButtonIcon, headlineIcon, headlineIconColor: red|green|blue|default }
        dangerousConfirm: (options) => new Promise((resolve) => {
            customAlertObject.initialize({
                headline: options.headline,
                actionCallback: resolve,
                boxType: 'dangerousConfirm',
                content: options.content,
                confirmButtonText: options.confirmButtonText || 'Confirm',
                confirmButtonIcon: options.confirmButtonIcon || null,
                cancelButtonText: options.cancelButtonText || 'Cancel',
                cancelButtonIcon: options.cancelButtonIcon || null,
                headlineIcon: options.headlineIcon,
                headlineIconColor: options.headlineIconColor
            });
        }),
        // for getting input from user with an input box
        // (result of promise will be like: {status: Boolean, value: String})
        // options: { headline, content, confirmButtonText, headlineIcon, headlineIconColor: red|green|blue|default }
        prompt: (options) => new Promise((resolve) => {
            customAlertObject.initialize({
                headline: options.headline,
                actionCallback: resolve,
                boxType: 'prompt',
                content: options.content,
                confirmButtonText: options.confirmButtonText || 'Confirm',
                confirmButtonIcon: options.confirmButtonIcon || null,
                headlineIcon: options.headlineIcon,
                headlineIconColor: options.headlineIconColor
            });
        }),
    }
    return customAlertMethods;
}

const Provider = props => (
    <CustomAlertProvider>
        <AlertComponent>
            {props.children}
        </AlertComponent>
    </CustomAlertProvider>
);

const AlertComponent = props => {

    const customAlert = useContext(CustomAlertContext);
    const alertDivRef = useRef(null);
    const alertDivBodyRef = useRef(null);
    const previouslyFocusedRef = useRef(null);
    const [promptText, setPromptText] = useState('');
    const location = useLocation();

    const closeAlert = () => {
        customAlert?.setStatus?.(false);
        customAlert.actionCallback?.(false);
        customAlert.closeCallback?.();
    };

    // remove on outside click
    useEffect(() => {
        if (!customAlert.isOpen) return undefined;

        const closeOnOutsideClick = e => {
            if (!alertDivBodyRef?.current?.contains?.(e.target)) {
                customAlert?.setStatus?.(false);
                customAlert.actionCallback?.(false);
                customAlert.closeCallback?.();
            }
        }

        // Attach on the next turn of the event loop, never synchronously. An alert
        // is almost always opened from a click handler, and React flushes this
        // effect while that same click is still bubbling up to document — so
        // attaching here and now would hand this listener the very click that
        // opened the alert and close it again before it has painted.
        const attachTimer = setTimeout(() => {
            document.addEventListener('click', closeOnOutsideClick);
        }, 0);

        return () => {
            clearTimeout(attachTimer);
            document.removeEventListener('click', closeOnOutsideClick);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customAlert.isOpen]);

    // eslint-disable-next-line arrow-body-style
    const iconColorLoader = useMemo(() => {
        return iconColorLoaderFunction(customAlert.headlineIconColor)
    }, [customAlert.headlineIconColor]);


    useEffect(() => {
        // To close the popup on url change (eg: going to a different page with the link in the popup)
        customAlert?.setStatus?.(false);
        customAlert.actionCallback?.(false);
        customAlert.closeCallback?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    // Focus management: move focus into the dialog on open, restore it on close.
    useEffect(() => {
        if (customAlert.isOpen) {
            previouslyFocusedRef.current = document.activeElement;
            const focusTimer = setTimeout(() => {
                alertDivBodyRef.current?.focus?.();
            }, 0);
            return () => clearTimeout(focusTimer);
        }
        if (previouslyFocusedRef.current?.focus) {
            previouslyFocusedRef.current.focus();
            previouslyFocusedRef.current = null;
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customAlert.isOpen]);

    // Close on Escape.
    useEffect(() => {
        if (!customAlert.isOpen) return undefined;
        const onKeyDown = e => {
            if (e.key === 'Escape') { closeAlert(); return; }
            // Focus trap (WCAG 2.1.2 / 2.4.3): keep Tab within the dialog.
            if (e.key === 'Tab' && alertDivBodyRef.current) {
                const focusables = Array.from(alertDivBodyRef.current.querySelectorAll(
                    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]),'
                    + ' select:not([disabled]), [tabindex]:not([tabindex="-1"])'
                )).filter(el => el.offsetParent !== null);
                if (!focusables.length) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                const active = document.activeElement;
                if (e.shiftKey && (active === first || active === alertDivBodyRef.current)) {
                    e.preventDefault();
                    last.focus();
                }
                else if (!e.shiftKey && active === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customAlert.isOpen]);


    return (
        <>
            {props.children}

            {customAlert.isOpen ? (
                <div className="custom-alert" ref={alertDivRef} data-testid="custom-alert-container">
                    <dialog
                        open
                        className="custom-alert_body"
                        ref={alertDivBodyRef}
                        tabIndex={-1}
                        role={['error', 'fail', 'confirm', 'dangerousConfirm', 'success', 'inform']
                            .includes(customAlert.boxType) ? 'alertdialog' : 'dialog'}
                        aria-modal="true"
                        aria-labelledby={customAlert.headline ? 'custom-alert-headline' : undefined}
                        aria-describedby={customAlert.content ? 'custom-alert-content' : undefined}>

                        {(customAlert.boxType === 'popup' || customAlert.headline) ? (
                            <div className={`custom-alert_body_header
                   ${customAlert.headline ? 'custom-alert_body_header__border-bottom' : ''}`}>
                                <h2 id="custom-alert-headline" className="custom-alert_body_header_headline">
                                    {customAlert.headlineIcon ?
                                        <i
                                            aria-hidden="true"
                                            className={`zelpicon ${customAlert.headlineIcon}
                                         ${iconColorLoader}`} /> : ''}
                                    {customAlert.headline || ''}
                                </h2>
                                {customAlert.boxType === 'popup' ? (
                                    <span
                                        data-testid="custom-alert-close"
                                        className="custom-alert_body_header_close"
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Close"
                                        onClick={() => {
                                            customAlert.setStatus(false);
                                            customAlert.closeCallback?.();
                                        }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                customAlert.setStatus(false);
                                                customAlert.closeCallback?.();
                                            }
                                        }}>
                                        <i
                                            aria-hidden="true"
                                            className="zelpicons zelpicons-close-remove-times"
                                            title="Close" />
                                    </span>) : ''}
                            </div>) : ''}

                        {customAlert.content || customAlert.boxType === 'prompt' ? (
                            <div id="custom-alert-content" className="custom-alert_body_content">
                                {customAlert.content}
                                {customAlert.boxType === 'prompt' ? (
                                    <div>
                                        <Input
                                            type="text"
                                            ariaLabel={customAlert.headline || 'Enter a value'}
                                            onChange={e => setPromptText(e.target.value.toString())} />
                                    </div>) : ''}
                            </div>) : ''}

                        <div className={`custom-alert_body_bottom
                                ${customAlert.boxType === 'confirm' ||
                                customAlert.boxType === 'dangerousConfirm' ||
                                customAlert.boxType === 'prompt' ? 'custom-alert_body_bottom__two-buttons' : ''}`}>

                            {(customAlert.boxType === 'confirm' ||
                                customAlert.boxType === 'dangerousConfirm' ||
                                customAlert.boxType === 'prompt') ?
                                (<Button
                                    type="default"
                                    size="xl"
                                    label={customAlert.cancelButtonText}
                                    icon={customAlert.cancelButtonIcon}
                                    iconVersion={2}
                                    onClick={() => {
                                        customAlert.setStatus(false);
                                        customAlert.actionCallback?.(false);
                                    }} />) : ''}

                            {(customAlert.boxType === 'confirm' || customAlert.boxType === 'prompt') ?
                                <Button
                                    type="cta"
                                    size="xl"
                                    label={customAlert.confirmButtonText}
                                    icon={customAlert.confirmButtonIcon}
                                    iconVersion={2}
                                    onClick={() => {
                                        customAlert.setStatus(false);
                                        customAlert.boxType === 'prompt' ? customAlert.actionCallback?.({
                                            status: true, value: promptText
                                        }) : customAlert.actionCallback?.(true);
                                    }} /> : ''}

                            {(customAlert.boxType === 'dangerousConfirm') ?
                                <Button
                                    type="danger"
                                    size="xl"
                                    label={customAlert.confirmButtonText}
                                    icon={customAlert.confirmButtonIcon}
                                    iconVersion={2}
                                    onClick={() => {
                                        customAlert.setStatus(false);
                                        customAlert.actionCallback?.(true);
                                    }} /> : ''}

                            {(customAlert.boxType === 'success' ||
                                customAlert.boxType === 'error' ||
                                customAlert.boxType === 'inform') ?
                                (<Button
                                    type="cta"
                                    size="xl"
                                    icon={customAlert.buttonIcon}
                                    label={customAlert.buttonText}
                                    iconVersion={2}
                                    onClick={() => {
                                        customAlert.setStatus(false);
                                    }} />) : ''}

                            {(customAlert.boxType === 'fail') ?
                                (
                                    <>
                                        <Button
                                            type="danger"
                                            size="xl"
                                            icon={customAlert.buttonIcon}
                                            label={customAlert.buttonText}
                                            iconVersion={2}
                                            onClick={() => {
                                                customAlert.setStatus(false);
                                                customAlert.closeCallback?.();
                                            }} />
                                        <Button
                                            type="default"
                                            size="xl"
                                            icon={customAlert.secondaryButtonIcon}
                                            label={customAlert.secondaryButtonText}
                                            iconVersion={2}
                                            onClick={() => {
                                                customAlert.setStatus(false);
                                                customAlert.secondaryCloseCallback?.();
                                            }} />
                                    </>) : ''}
                        </div>
                    </dialog>
                </div>) : ''}
        </>
    );
}

const iconColorLoaderFunction = (color) => {
    if (color === 'red') {
        return 'custom-alert_body_header_headline_icon__red';
    }
    else if (color === 'green') {
        return 'custom-alert_body_header_headline_icon__green';
    }
    else if (color === 'blue') {
        return 'custom-alert_body_header_headline_icon__blue';
    }
    else {
        return '';
    }
}

export { Provider, useCustomAlert, AlertComponent };
